import type { TechnologiesFilterDto } from "app/pages/sales-intelligence/domain/technology/types/filter-values";
import type { GroupedTechnologiesCategoryMap } from "app/pages/sales-intelligence/domain/technology/types/model";
import type {
    TechColumnName,
} from "app/pages/sales-intelligence/sub-modules/common/services/company-search-column-suggestions/types/columns";
import {
    ColumnType,
} from "app/pages/sales-intelligence/sub-modules/common/services/company-search-column-suggestions/types/columns";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import { createTechColumnName } from "../misc";

import { getSubCategoriesColumnNames } from "./getSubCategoriesColumnName";
import { getTechnologiesColumnName } from "./getTechnologiesColumnName";

type GroupedTechnologies = {
    parentCategories: string[];
    subCategories: string[];
    technologies: string[];
};

const createGroupedTechnologiesPool = (
    technologiesFilterDtos: readonly TechnologiesFilterDto[],
): GroupedTechnologies => {
    const initialGroupedTechnologies: GroupedTechnologies = {
        parentCategories: [],
        subCategories: [],
        technologies: [],
    };

    return technologiesFilterDtos.reduce<GroupedTechnologies>((acc, filterDto) => {
        if (filterDto.inclusion === inclusionOptionMap.includeOnly) {
            acc.parentCategories.push(...filterDto.parentCategories);
            acc.subCategories.push(...filterDto.categories);
            acc.technologies.push(...filterDto.technologies);
        }

        return acc;
    }, initialGroupedTechnologies);
};

export const processTechnologiesFilter = (
    technologiesFilterDto: readonly TechnologiesFilterDto[],
    technologiesCategoryMap: GroupedTechnologiesCategoryMap,
): readonly TechColumnName[] => {
    const columnsNames = new Set<TechColumnName>();

    const filterTechnologiesPool = createGroupedTechnologiesPool(technologiesFilterDto);

    filterTechnologiesPool.parentCategories.forEach(
        (c) => c && columnsNames.add(createTechColumnName(ColumnType.TechCategory, c)),
    );

    if (filterTechnologiesPool.subCategories.length !== 0) {
        const subCategoriesColumnNames = getSubCategoriesColumnNames(
            filterTechnologiesPool.subCategories,
            technologiesCategoryMap.subCategories,
        );

        subCategoriesColumnNames.forEach((c) => c && columnsNames.add(c));
    }

    if (filterTechnologiesPool.technologies.length > 1) {
        const technologiesColumnName = getTechnologiesColumnName(
            filterTechnologiesPool.technologies,
            technologiesCategoryMap,
        );

        if (technologiesColumnName) {
            columnsNames.add(technologiesColumnName);
        }
    }

    return Array.from(columnsNames);
};
